import {useEffect, useState, useMemo} from 'react';
import {useTelegramTheme} from '../../../shared/utils/telegram';
import {api} from '../../../shared/services/api';
import {CircularProgressbar, buildStyles} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';


function SystemStats() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);

    const themeParams = useTelegramTheme();

    // Map new theme params to old naming convention for compatibility
    const theme = {
        text_color: themeParams.textColor,
        hint_color: themeParams.hintColor,
        link_color: themeParams.linkColor,
        section_bg_color: themeParams.sectionBackgroundColor,
        section_separator_color: themeParams.sectionSeparatorColor,
    };

    // Вычисляем средний процент использования диска
    // ВАЖНО: useMemo должен быть ДО условных return statements
    const averageDiskUsage = useMemo(() => {
        if (!stats || !stats.disk || stats.disk.length === 0) return 0;
        const total = stats.disk.reduce((sum, disk) => sum + disk.usagePercent, 0);
        return Math.round(total / stats.disk.length);
    }, [stats]);

    // Функция для получения цвета в зависимости от процента использования - мемоизируем цвета
    // ВАЖНО: useMemo должен быть ДО условных return statements
    const themeColors = useMemo(() => {
        const buttonColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--tg-theme-button-color').trim();
        const destructiveColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--tg-theme-destructive-text-color').trim();

        return {
            button: buttonColor || '#007aff',
            destructive: destructiveColor || '#ff3b30'
        };
    }, []); // Вычисляем только один раз при монтировании

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.get('/api/stats/system');
                setStats(response);

                // Add to history (keep last 20 points)
                setHistory(prev => {
                    const newHistory = [...prev, {
                        time: new Date().toLocaleTimeString(),
                        cpu: response.cpu.usage,
                        memory: response.memory.usagePercent,
                    }];
                    return newHistory.slice(-20);
                });

                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
                setLoading(false);
            }
        };

        fetchStats();
        const interval = setInterval(fetchStats, 5000); // Update every 5 seconds

        return () => clearInterval(interval);
    }, []);

    // Условные returns ПОСЛЕ всех хуков
    if (loading) {
        return (
            <div style={{color: theme.text_color}} className="p-4">
                Загрузка статистики...
            </div>
        );
    }

    if (!stats) {
        return (
            <div style={{color: theme.hint_color}} className="p-4">
                Не удалось загрузить статистику
            </div>
        );
    }

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getProgressColor = (percent) => {
        // Плавный переход от синего к красному начиная с 60%
        if (percent < 60) {
            return themeColors.button;
        } else {
            // Плавная интерполяция от button color к destructive
            const ratio = (percent - 60) / 40; // от 60% до 100%
            return `color-mix(in srgb, ${themeColors.destructive} ${ratio * 100}%, ${themeColors.button} ${(1 - ratio) * 100}%)`;
        }
    };

    return (
        <div className="relative flex flex-col justify-center space-y-4">
            {/* CPU & RAM Usage */}
            <div
                className="rounded-lg border p-4 bg-theme-section-bg-color text-theme-text-color border-theme-section-separator-color"
            >
                <div className={`flex gap-6`}>
                    <CircularProgressbar
                        value={stats.cpu.usage}
                        text={`CPU ${stats.cpu.usage}%`}
                        strokeWidth={14}
                        styles={buildStyles({
                            textSize: '12px',
                            pathTransitionDuration: 0.5,
                            pathColor: getProgressColor(stats.cpu.usage),
                            textColor: `var(--tg-theme-text-color)`,
                            trailColor: '#d6d6d6',
                            backgroundColor: '#3e98c7',
                        })}
                    />
                    <CircularProgressbar
                        value={stats.memory.usagePercent}
                        text={`RAM ${stats.memory.usagePercent}%`}
                        strokeWidth={14}
                        styles={buildStyles({
                            textSize: '12px',
                            pathTransitionDuration: 0.5,
                            pathColor: getProgressColor(stats.memory.usagePercent),
                            textColor: `var(--tg-theme-text-color)`,
                            trailColor: '#d6d6d6',
                            backgroundColor: '#3e98c7',
                        })}
                    />
                    <CircularProgressbar
                        value={averageDiskUsage}
                        text={`Disk ${averageDiskUsage}%`}
                        strokeWidth={14}
                        styles={buildStyles({
                            textSize: '12px',
                            pathTransitionDuration: 0.5,
                            pathColor: getProgressColor(averageDiskUsage),
                            textColor: `var(--tg-theme-text-color)`,
                            trailColor: '#d6d6d6',
                            backgroundColor: '#3e98c7',
                        })}
                    />

                </div>
            </div>


        </div>
    );
}

export default SystemStats;
